import {User as Auth0UserModel} from '@auth0/auth0-spa-js';
import {getData, setData} from '@/utils';
import {type AuthModel, UserModel} from './_models';
import {useNavigate} from 'react-router-dom'; // Import useNavigate for redirecting
import {enqueueSnackbar} from 'notistack'; // Assuming you are using notistack for notifications

const AUTH_LOCAL_STORAGE_KEY = `${import.meta.env.VITE_APP_NAME}-auth-v${
    import.meta.env.VITE_APP_VERSION
}`;

const getAuth = (): AuthModel | undefined => {
    try {
        const auth = getData(AUTH_LOCAL_STORAGE_KEY) as AuthModel | undefined;

        if (auth) {
            return auth;
        } else {
            return undefined;
        }
    } catch (error) {
        console.error('AUTH LOCAL STORAGE PARSE ERROR', error);
    }
};

const setAuth = (auth: AuthModel | Auth0UserModel) => {
    setData(AUTH_LOCAL_STORAGE_KEY, auth);
};

const removeAuth = () => {
    if (!localStorage) {
        return;
    }

    try {
        localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY);
    } catch (error) {
        console.error('AUTH LOCAL STORAGE REMOVE ERROR', error);
    }
};

export function setupAxios(axios: any) {
    axios.defaults.headers.Accept = 'application/json';

    // Interceptor for adding Authorization header
    axios.interceptors.request.use(
        (config: { headers: { Authorization: string } }) => {
            const auth = getAuth();

            if (auth?.access_token) {
                config.headers.Authorization = `Bearer ${auth.access_token}`;
            }

            return config;
        },
        async (err: any) => await Promise.reject(err)
    );

    // Interceptor to handle the 401 status and redirect to the login page
    axios.interceptors.response.use(
        (response: any) => response, // Pass through successful responses
        (error: any) => {
            if (error.response && error.response.status === 401) {
                // When a 401 error is caught, redirect to login page
                const navigate = useNavigate(); // Initialize navigate
                navigate('/login'); // Redirect to login page

                // Show a notification to the user
                enqueueSnackbar('Session expired. Please log in again.', {
                    variant: 'solid',
                    state: 'danger',
                });
            }
            return Promise.reject(error); // Reject the promise after handling the error
        }
    );
}

export function hasRole(user: UserModel | undefined, role: string): boolean {
    return user?.roles?.includes(role) ?? false;
}

export function hasRoleFromList(roles: string[] | undefined, role: string): boolean {
    return roles?.includes(role) ?? false;
}

export {AUTH_LOCAL_STORAGE_KEY, getAuth, removeAuth, setAuth};
